import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider } from '@material-ui/core/styles';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GetFundScenariosPage } from 'modules/graphql';
import { GET_FUND_NAME } from 'modules/fund/queries';
import { AddFundScenarios } from 'modules/graphql';
import { useStores } from 'hooks/useStores';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { WsMessageTypes } from 'constants/ws';
import { useSnackbar } from 'hooks/useSnackbar';

import { columns } from './components/portfolioTable/columns';
import { Actual } from './components/actual';
import { ScenariosPanel } from './components/scenarios';
import { CollapsibleTable } from './components/portfolioTable';
import { ScenarioOnboardingModal } from './components/scenarios/ScenariosOnboardingModal';
import ScenariosTheme from './ScenariosTheme';

import './style.scss';

export const ScenariosPage = () => {
  const { fundId } = useParams<{ fundId: string }>();

  const { fundScenarioStore } = useStores();

  const [fundCurrencySign, setFundCurrencySign] = useState<string>();
  const [fundName, setFundName] = useState(true);
  const [gridColumns, setGridColumns] = useState<any[]>([]);
  const [isOnboardingModalOpen, setOnboardingModalOpen] = useState(false);
  const [onScenarioChange, setOnScenarioChange] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [ws, setWs] = useState<ReconnectingWebSocket>();
  const timer = useRef<ReturnType<typeof setTimeout>>(null);

  const { addSnackbar } = useSnackbar();

  const [addFundScenarios] = useMutation(AddFundScenarios);

  //todo: @vmyshko: display errors
  const { refetch, loading } = useQuery(GetFundScenariosPage, {
    variables: { id: fundId },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      fundScenarioStore.setIsCalculated(true);
      const errorOccurred = data.fundScenarios?.some(
        (fundScenario: any) => fundScenario.calculationStatus === 'ERROR',
      );

      if (errorOccurred) {
        addSnackbar('Failed to calculate scenarios');
      }

      setScenarios(
        data.fundScenarios
          .slice()
          .sort((a: any, b: any) => (a.position > b.position ? 1 : -1)),
      );

      if (data && !data.fundScenarios.length) {
        addFundScenarios({
          variables: {
            scenarioSets: [
              {
                fundId: fundId,
                scenarios: [1, 2, 3].map((s, i) => {
                  return {
                    name: 'Scenario #' + s,
                    position: i,
                    defaultExitDate: '2022-01-01',
                    defaultDilution: 0,
                  };
                }),
              },
            ],
          },
        })
          .then((addResponse) => {
            if (addResponse) {
              refetch().then((results) =>
                setScenarios(results.data.fundScenarios),
              );
            }
          })
          .catch((err) => console.error('Failed to add default scenario', err));
      }
    },
  });

  useQuery(GET_FUND_NAME, {
    variables: { id: fundId },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      document.title = `Diligent Equity Invest - ${data.fund.name}`;
      setFundName(data.fund.name);
      setFundCurrencySign(window.GLOBAL_CONFIG.CURRENCIES[data.fund.currency].sign);
    },
  });

  useEffect(() => {
    if (ws === undefined) {
      if (!fundId) throw new Error('No fundId');

      setWs(
        new ReconnectingWebSocket(
          `${window.location.protocol === 'http:' ? 'ws' : 'wss'}://${
            window.location.host
          }/ws/fund-scenarios/${fundId}/`,
        ),
      );
    } else {
      ws.onmessage = (msg) => {
        const message = JSON.parse(msg.data).message;
        if (message.type === WsMessageTypes.ERROR) {
          console.warn('WS error message', msg.data);
          addSnackbar('Failed to calculate scenarios');
        }
        if (timer.current) {
          clearTimeout(timer.current);
        }
        // @ts-ignore
        timer.current = setTimeout(() => {
          refetch();
        }, 2000);
      };
    }

    const shouldOnboardingModalBeOpen = window.localStorage.getItem(
      'shouldOnboardingModalBeOpen',
    );
    if (shouldOnboardingModalBeOpen === null) {
      setOnboardingModalOpen(true);
    }
  });

  useEffect(() => {
    if (fundCurrencySign) {
      const cols = columns(fundCurrencySign);
      setGridColumns(cols);
    }
  }, [fundCurrencySign]);

  useEffect(() => {
    fundScenarioStore.setIsLoading(loading);
  }, [loading]);

  function handleSkipOrFinish() {
    //@ts-ignore
    window.localStorage.setItem('shouldOnboardingModalBeOpen', false);
    setOnboardingModalOpen(false);
  }

  const isLensUI = Boolean(window.GLOBAL_CONFIG.FLIPPERS.LENS_UI);
  return (
    <ThemeProvider
      theme={(commonTheme) => ({ ...commonTheme, ...(isLensUI ? [] : ScenariosTheme) })}
    >
      <div className="d-flex justify-content-center">
        <ScenarioOnboardingModal
          open={isOnboardingModalOpen}
          onClose={() => setOnboardingModalOpen(false)}
          onSkip={handleSkipOrFinish}
          onFinish={handleSkipOrFinish}
          onNext={() =>
            //@ts-ignore
            window.localStorage.setItem('shouldOnboardingModalBeOpen', false)
          }
        />
        <div
          style={{ maxWidth: 1240 }}
          className="d-flex flex-column w-100 p-2"
        >
          <div className="d-flex">
            <div
              style={{ width: 280 }}
              className="p-2 d-flex flex-shrink-0 flex-grow-1"
            >
              <Actual fundId={fundId} />
            </div>
            <div className="p-2 d-flex w-100">
              <ScenariosPanel
                fundId={fundId}
                fundCurrencySign={fundCurrencySign}
                scenarios={scenarios}
                onScenarioChange={setOnScenarioChange}
                refetch={refetch}
              />
            </div>
          </div>
          <div className="h-100 p-2">
            <CollapsibleTable
              columns={gridColumns}
              fundId={fundId}
              fundName={fundName}
              scenarios={scenarios}
              fundCurrencySign={fundCurrencySign}
              onScenarioChange={onScenarioChange}
            />
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};
